import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '@/lib/api';
import { ChevronRight, Plus } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import AddProfileModal from '@/components/AddProfileModal';

/* ──────────────────────────────────────────────────────────────────────────
   Stats Page — TEAM (default) or PLAYER mode toggle.
   • TEAM: aggregates from MY_TEAM + BOTH_TEAMS games (excludes MY_PLAYER).
   • PLAYER: roster list, taps through to player card.
   ────────────────────────────────────────────────────────────────────────── */

const NAVY = '#0B1824';
const ORANGE = '#e8963a';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';
const GOOD = '#198754';
const POOR = '#CE1141';

/* Performance bands — number-only color (no tile bg). */
function colorFor(stat, value) {
  if (value == null || Number.isNaN(value)) return NAVY;
  switch (stat) {
    case '2pt':    return value >= 48 ? GOOD : POOR;
    case '3pt':    return value >= 30 ? GOOD : POOR;
    case 'ft':     return value >= 75 ? GOOD : POOR;
    case 'fg':     return value >= 45 ? GOOD : POOR;
    case 'efg':    return value >= 48 ? GOOD : POOR;
    case 'orb':    return value >= 20 ? GOOD : POOR;
    case 'ftr':    return value >= 20 ? GOOD : POOR;
    case 'ast':    return value >= 50 ? GOOD : POOR;
    case 'oppAst': return value <= 60 ? GOOD : POOR;
    case 'tov':    return value <= 15 ? GOOD : POOR;
    default:       return NAVY;
  }
}

const fmtPct = (v, d = 1) => (v == null || Number.isNaN(v) ? null : `${v.toFixed(d)}%`);
const fmtRatio = (v) => (v == null || Number.isNaN(v) ? null : v.toFixed(2));

/* ── Filter pill ── */
function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="h-10 px-4 rounded-full text-[13px] font-bold uppercase tracking-[.07em] transition-all active:scale-[.97]"
      style={{
        fontFamily: "'Barlow Condensed', sans-serif",
        background: active ? NAVY : '#F1F5F9',
        color: active ? '#fff' : 'rgba(0,0,0,0.45)',
        border: `1px solid ${active ? NAVY : 'rgba(0,0,0,0.1)'}`,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

/* ── Stat tile (white, color number only) ── */
function StatTile({ label, value, color, testid }) {
  const isNA = value == null;
  return (
    <div
      data-testid={testid}
      className="rounded-[12px] p-4 flex flex-col items-center justify-center"
      style={{ background: '#fff', border: `1.5px solid ${BORDER}`, minHeight: 116 }}
    >
      <div
        className="text-[13px] font-bold uppercase tracking-[.10em] mb-2"
        style={{ fontFamily: "'Barlow Condensed', sans-serif", color: MUTED }}
      >
        {label}
      </div>
      {isNA ? (
        <div className="text-[22px] font-semibold" style={{ fontFamily: "'Barlow Condensed', sans-serif", color: '#cbd5e1', letterSpacing: '.04em' }}>
          NA
        </div>
      ) : (
        <div className="text-[36px] leading-none tracking-tight"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color }}
        >
          {value}
        </div>
      )}
    </div>
  );
}

export default function Stats() {
  const { selectedTeamId, teams = [] } = useOutletContext() || {};
  const navigate = useNavigate();
  const [view, setView] = useState('team');                 // 'team' | 'players' | 'favorites'
  const [profiles, setProfiles] = useState([]);             // user's profile shells
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [addProfileOpen, setAddProfileOpen] = useState(false);
  const [filter, setFilter] = useState('last5');
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [teamGames, setTeamGames] = useState([]);          // MY_TEAM + BOTH_TEAMS (legacy null OK)
  const [playerGames, setPlayerGames] = useState([]);      // MY_PLAYER + BOTH_TEAMS (for player stats)
  const [boxScores, setBoxScores] = useState({});          // game_id -> {home,away,is_home}
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);

  const team = teams.find(t => t.team_id === selectedTeamId);

  /* ── Fetch games (both filtered sets) + roster ── */
  useEffect(() => {
    if (!selectedTeamId) { setTeamGames([]); setPlayerGames([]); setRoster([]); setBoxScores({}); return; }
    setLoading(true);
    (async () => {
      try {
        const [gRes, pRes] = await Promise.all([
          api.get(`/teams/${selectedTeamId}/games`),
          api.get(`/teams/${selectedTeamId}/players`).catch(() => ({ data: [] })),
        ]);
        const completed = (gRes.data || [])
          .filter(g => g.status === 'final' || g.status === 'locked')
          .sort((a, b) => new Date(b.date_time) - new Date(a.date_time));
        const teamSet = completed.filter(g => !g.tracking_mode || g.tracking_mode === 'MY_TEAM' || g.tracking_mode === 'BOTH_TEAMS');
        const playerSet = completed.filter(g => g.tracking_mode === 'MY_PLAYER' || g.tracking_mode === 'BOTH_TEAMS');
        setTeamGames(teamSet);
        setPlayerGames(playerSet);
        setRoster((pRes.data || []).filter(p => p.active !== false));
      } catch (e) {
        console.error('Failed to fetch games/roster:', e);
      } finally { setLoading(false); }
    })();
  }, [selectedTeamId]);

  /* ── Fetch profile shells whenever the user enters that tab ── */
  const refreshProfiles = () => {
    setProfilesLoading(true);
    api.get('/profiles')
      .then(r => setProfiles(r.data?.profiles || []))
      .catch(() => setProfiles([]))
      .finally(() => setProfilesLoading(false));
  };
  useEffect(() => {
    if (view !== 'favorites') return;
    refreshProfiles();
  }, [view]);

  /* ── Window filter (applies to both view modes for consistency) ── */
  const sourceGames = view === 'team' ? teamGames : playerGames;
  const filteredGames = useMemo(() => {
    if (!sourceGames.length) return [];
    if (filter === 'last5')  return sourceGames.slice(0, 5);
    if (filter === 'last10') return sourceGames.slice(0, 10);
    if (filter === 'range') {
      const from = dateFrom ? new Date(dateFrom) : null;
      const to   = dateTo   ? new Date(dateTo + 'T23:59:59') : null;
      return sourceGames.filter(g => {
        const d = new Date(g.date_time);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    return sourceGames; // all-time
  }, [sourceGames, filter, dateFrom, dateTo]);

  /* ── Fetch box scores for filtered games ── */
  useEffect(() => {
    const missing = filteredGames.filter(g => !boxScores[g.game_id]);
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const results = {};
      await Promise.all(missing.map(async (g) => {
        try {
          const r = await api.get(`/games/${g.game_id}/box-scores`);
          // Pick the bucket matching the team's side so away games
          // surface their per-player rows correctly. Reading
          // `home.players` unconditionally was the bug that hid every
          // away-game roster aggregate, imports and live tracks alike.
          const ourPlayers = (g.is_home ? r.data?.home?.players : r.data?.away?.players) || [];
          results[g.game_id] = {
            home: r.data?.home?.totals || null,
            away: r.data?.away?.totals || null,
            team_players: ourPlayers,
            is_home: g.is_home,
          };
        } catch { results[g.game_id] = null; }
      }));
      if (!cancelled) setBoxScores(prev => ({ ...prev, ...results }));
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredGames]);

  /* ── TEAM aggregate (existing math) ── */
  const teamAggregate = useMemo(() => {
    const z = () => ({ fgm:0, fga:0, three_pm:0, three_pa:0, ftm:0, fta:0, oreb:0, dreb:0, ast:0, to:0 });
    const us = z(); const opp = z();
    let usable = 0;
    for (const g of filteredGames) {
      const bs = boxScores[g.game_id];
      if (!bs || !bs.home || !bs.away) continue;
      usable += 1;
      const ourSide = g.is_home ? bs.home : bs.away;
      const oppSide = g.is_home ? bs.away : bs.home;
      const add = (t, s) => {
        ['fgm','fga','three_pm','three_pa','ftm','fta','oreb','dreb','ast','to'].forEach(k => { t[k] += s[k] || 0; });
      };
      add(us, ourSide); add(opp, oppSide);
    }
    return { us, opp, usable };
  }, [filteredGames, boxScores]);

  const teamStats = useMemo(() => {
    const { us, opp } = teamAggregate;
    const safe = (n, d) => (d > 0 ? (n / d) * 100 : null);
    const us2pm = us.fgm - us.three_pm;
    const us2pa = us.fga - us.three_pa;
    return {
      twoPct: safe(us2pm, us2pa),
      threePct: safe(us.three_pm, us.three_pa),
      ftPct: safe(us.ftm, us.fta),
      fgPct: safe(us.fgm, us.fga),
      eFG: us.fga > 0 ? ((us.fgm + 0.5 * us.three_pm) / us.fga) * 100 : null,
      orbPct: (us.oreb + opp.dreb) > 0 ? (us.oreb / (us.oreb + opp.dreb)) * 100 : null,
      ftr: us.fga > 0 ? (us.fta / us.fga) * 100 : null,
      astPct: us.fgm > 0 ? (us.ast / us.fgm) * 100 : null,
      oppAst: opp.fgm > 0 ? (opp.ast / opp.fgm) * 100 : null,
      tovPct: (us.fga + 0.44 * us.fta + us.to) > 0 ? (us.to / (us.fga + 0.44 * us.fta + us.to)) * 100 : null,
    };
  }, [teamAggregate]);

  /* ── Per-player tracked-game count (for roster list) ── */
  const playerGameCounts = useMemo(() => {
    const counts = {};
    for (const g of filteredGames) {
      const bs = boxScores[g.game_id];
      if (!bs || !bs.team_players) continue;
      bs.team_players.forEach(p => {
        if (!p.player_id) return;
        const hasStats = (p.fga || p.fta || p.pts || p.ast || p.stl || p.blk || p.defl || p.oreb || p.dreb || p.to);
        if (hasStats) counts[p.player_id] = (counts[p.player_id] || 0) + 1;
      });
    }
    return counts;
  }, [filteredGames, boxScores]);

  /* ── Render ── */
  return (
    <div className="max-w-3xl mx-auto" data-testid="stats-page">
      <h1 className="text-[28px] sm:text-[32px] font-extrabold uppercase tracking-tight text-slate-900 mb-3"
          style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          data-testid="stats-title">
        Stats
      </h1>

      {/* TEAM / PLAYERS / FAVORITES toggle */}
      <div className="flex gap-2 mb-4" data-testid="stats-view-toggle">
        {[
          { key: 'team',       label: 'Team' },
          { key: 'players',    label: 'Players' },
          { key: 'favorites',  label: 'Favorites' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setView(t.key)}
            data-testid={`stats-view-${t.key}`}
            className="flex-1 h-11 rounded-full text-[14px] font-bold uppercase tracking-[.07em] transition-all active:scale-[.98]"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              background: view === t.key ? NAVY : '#fff',
              color: view === t.key ? '#fff' : NAVY,
              border: `1.5px solid ${view === t.key ? NAVY : BORDER}`,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Consolidated filter bar — pills only, "CUSTOM RANGE" replaces the icon button */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="stats-filters">
        <FilterPill active={filter === 'last5'}  onClick={() => setFilter('last5')}  testid="filter-last5">Last 5</FilterPill>
        <FilterPill active={filter === 'last10'} onClick={() => setFilter('last10')} testid="filter-last10">Last 10</FilterPill>
        <FilterPill active={filter === 'all'}    onClick={() => setFilter('all')}    testid="filter-all">All Time</FilterPill>
        <FilterPill active={filter === 'range'}  onClick={() => { setFilter('range'); setShowRangePicker(true); }} testid="filter-range">Custom Range</FilterPill>
      </div>

      {/* TEAM view */}
      {view === 'team' && (
        <>
          {(() => {
            if (loading) {
              return <p className="text-[13px] mb-2.5" style={{ color: MUTED }} data-testid="stats-sample-note">Loading…</p>;
            }
            const count = teamAggregate.usable;
            const target = filter === 'last5' ? 5 : filter === 'last10' ? 10 : null;
            let copy = null;
            if (count === 0) {
              copy = 'No Team Games Logged Yet';
            } else if (target && count < target) {
              copy = `Only ${count} Team Game${count === 1 ? '' : 's'} Logged`;
            }
            return copy
              ? <p className="text-[13px] mb-2.5" style={{ color: MUTED }} data-testid="stats-sample-note">{copy}</p>
              : null;
          })()}

          <div className="grid grid-cols-2 gap-2.5 sm:gap-3" data-testid="stats-grid">
            <StatTile label="2PT%"     value={fmtPct(teamStats.twoPct)}   color={colorFor('2pt',    teamStats.twoPct)}   testid="stat-2pt" />
            <StatTile label="3PT%"     value={fmtPct(teamStats.threePct)} color={colorFor('3pt',    teamStats.threePct)} testid="stat-3pt" />
            <StatTile label="FT%"      value={fmtPct(teamStats.ftPct)}    color={colorFor('ft',     teamStats.ftPct)}    testid="stat-ft"  />
            <StatTile label="FG%"      value={fmtPct(teamStats.fgPct)}    color={colorFor('fg',     teamStats.fgPct)}    testid="stat-fg"  />
            <StatTile label="eFG%"     value={fmtPct(teamStats.eFG)}      color={colorFor('efg',    teamStats.eFG)}      testid="stat-efg" />
            <StatTile label="ORB%"     value={fmtPct(teamStats.orbPct)}   color={colorFor('orb',    teamStats.orbPct)}   testid="stat-orb" />
            <StatTile label="FTR"      value={fmtPct(teamStats.ftr)} color={colorFor('ftr', teamStats.ftr)} testid="stat-ftr" />
            <StatTile label="AST%"     value={fmtPct(teamStats.astPct)}   color={colorFor('ast',    teamStats.astPct)}   testid="stat-ast" />
            <StatTile label="Opp AST%" value={fmtPct(teamStats.oppAst)}   color={colorFor('oppAst', teamStats.oppAst)}   testid="stat-opp-ast" />
            <StatTile label="TOV%"     value={fmtPct(teamStats.tovPct)}   color={colorFor('tov',    teamStats.tovPct)}   testid="stat-tov" />
          </div>
        </>
      )}

      {/* PLAYERS view (current team's roster list) */}
      {view === 'players' && (
        <>
          {loading && <p className="text-[13px] mb-2.5" style={{ color: MUTED }} data-testid="stats-sample-note-player">Loading…</p>}

          {!loading && roster.length === 0 ? (
            <div className="rounded-xl px-4 py-10 text-center"
              style={{ background: '#fff', border: `1.5px dashed ${BORDER}` }}
              data-testid="player-roster-empty">
              <p className="text-[14px] font-medium" style={{ color: NAVY }}>No roster yet</p>
              <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                Add players from the Teams page to see per-player stats.
              </p>
            </div>
          ) : !loading && (
            <div className="space-y-2" data-testid="player-roster-list">
              {roster.map(p => (
                <button
                  key={p.player_id}
                  onClick={() => navigate(`/stats/player/${p.player_id}`)}
                  data-testid={`roster-row-${p.player_id}`}
                  className="w-full rounded-xl p-3.5 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                  style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                >
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 18 }}>
                    {p.jersey_number || '–'}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-[18px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                      {p.name || 'Unnamed Player'}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5" style={{ color: MUTED }} />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {/* FAVORITES view (user's Player Profile shells) */}
      {view === 'favorites' && (
        <>
          <button
            onClick={() => setAddProfileOpen(true)}
            data-testid="add-player-profile-btn"
            className="w-full mb-3 rounded-xl px-4 h-14 flex items-center justify-center gap-2 transition-all active:scale-[.99]"
            style={{
              background: NAVY,
              color: '#fff',
              fontFamily: "'Barlow Condensed', sans-serif",
              fontWeight: 800,
              letterSpacing: '.08em',
              fontSize: 15,
            }}
          >
            <Plus className="w-5 h-5" /> ADD PLAYER PROFILE
          </button>

          {profilesLoading && (
            <p className="text-[13px] mb-2.5" style={{ color: MUTED }} data-testid="profiles-loading">Loading…</p>
          )}

          {!profilesLoading && profiles.length === 0 ? (
            <div className="rounded-xl px-4 py-10 text-center"
              style={{ background: '#fff', border: `1.5px dashed ${BORDER}` }}
              data-testid="profiles-empty">
              <p className="text-[14px] font-medium" style={{ color: NAVY }}>No player profiles yet</p>
              <p className="text-[12px] mt-1" style={{ color: MUTED }}>
                Tap "Add Player Profile" to bundle a player&apos;s stats from
                multiple teams into one consolidated view.
              </p>
            </div>
          ) : !profilesLoading && (
            <div className="space-y-2" data-testid="profiles-list">
              {profiles.map(p => {
                const teamLine = (p.members || [])
                  .map(m => m.team_name).filter(Boolean).join(' · ');
                return (
                  <button
                    key={p.profile_id}
                    onClick={() => navigate(`/stats/profile/${p.profile_id}`)}
                    data-testid={`profile-row-${p.profile_id}`}
                    className="w-full rounded-xl p-3.5 flex items-center gap-3 transition-all hover:bg-slate-50 active:scale-[.99]"
                    style={{ background: '#fff', border: `1.5px solid ${BORDER}` }}
                  >
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 18 }}>
                      {p.jersey_number || '–'}
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className="text-[18px] font-bold truncate leading-tight" style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {p.name}
                      </p>
                      <p className="text-[11px] mt-0.5 truncate uppercase tracking-[.06em]" style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>
                        {(p.members?.length || 0)} TEAM{(p.members?.length || 0) === 1 ? '' : 'S'}
                        {teamLine ? ` · ${teamLine}` : ''}
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5" style={{ color: MUTED }} />
                  </button>
                );
              })}
            </div>
          )}

          <AddProfileModal
            open={addProfileOpen}
            onOpenChange={setAddProfileOpen}
            onCreated={(profile) => {
              refreshProfiles();
              navigate(`/stats/profile/${profile.profile_id}`);
            }}
          />
        </>
      )}

      {/* Date range bottom-sheet (shadcn dialog already standard'd to bottom sheet in this app) */}
      <Dialog open={showRangePicker} onOpenChange={setShowRangePicker}>
        <DialogContent className="max-w-md" data-testid="date-range-modal">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              Date Range
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[.12em] mb-1"
                style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>From</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from"
                className="w-full h-10 px-3 rounded-full border-[1.5px] text-[14px]"
                style={{ borderColor: BORDER, fontFamily: "'Barlow Condensed', sans-serif" }} />
            </div>
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-[.12em] mb-1"
                style={{ color: MUTED, fontFamily: "'Barlow Condensed', sans-serif" }}>To</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to"
                className="w-full h-10 px-3 rounded-full border-[1.5px] text-[14px]"
                style={{ borderColor: BORDER, fontFamily: "'Barlow Condensed', sans-serif" }} />
            </div>
            <button
              onClick={() => setShowRangePicker(false)}
              data-testid="date-range-apply"
              className="w-full h-11 rounded-full text-[14px] font-bold uppercase tracking-[.08em] mt-2"
              style={{ background: NAVY, color: '#fff', fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Apply
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* expose team for downstream player card via sessionStorage shim */}
      <input type="hidden" data-team-name={team?.name || ''} data-team-level={team?.level || ''} />
    </div>
  );
}
